import { normalizeZoneDeviceEntryFromApi } from "app/pages/customers/dsok/access-system/zoneApiDeviceShape";

/** Default zoneAccess when missing on product (ticket). */
export const DEFAULT_ZONE_ACCESS = {
  zones: [],
  allowTurnstile: true,
  allowGate: true,
  deviceOverrides: [],
};

const ENTRY_SINGLE = "single_entry";
const ENTRY_MULTI = "multi_entry";

const RULE_DENY = "deny";
const RULE_SINGLE_USE = "single_use";
const RULE_MULTI_USE = "multi_use";

export function getZoneIdFromApiRow(z) {
  if (!z || typeof z !== "object") return "";
  const id = z._id != null ? z._id : z.id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export function getZoneLabelFromApiRow(z) {
  if (!z || typeof z !== "object") return "";
  const name =
    typeof z.name === "string" && z.name.trim() !== ""
      ? z.name.trim()
      : getZoneIdFromApiRow(z);
  return name || getZoneIdFromApiRow(z);
}

/** @param {*} raw @returns {"single_entry"|"multi_entry"} */
export function normalizeZoneEntryRule(raw) {
  const s = String(raw || "").trim();
  return s === ENTRY_MULTI ? ENTRY_MULTI : ENTRY_SINGLE;
}

export function normalizeOverrideRule(raw) {
  const s = String(raw || "").trim();
  if (s === RULE_DENY || s === RULE_SINGLE_USE || s === RULE_MULTI_USE) {
    return s;
  }
  return null;
}

export function normalizeDeviceOverridesArray(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const deviceId = String(row.deviceId ?? "").trim();
    if (!deviceId) continue;
    const rule = normalizeOverrideRule(row.rule);
    if (!rule) continue;
    if (seen.has(deviceId)) continue;
    seen.add(deviceId);
    out.push({ deviceId, rule });
  }
  return out;
}

function normalizeZonesArrayFromNewShape(zonesRaw) {
  if (!Array.isArray(zonesRaw)) return [];
  const out = [];
  const seen = new Set();
  for (const row of zonesRaw) {
    if (!row || typeof row !== "object") continue;
    const zoneId = String(row.zoneId ?? "").trim();
    if (!zoneId || seen.has(zoneId)) continue;
    seen.add(zoneId);
    out.push({
      zoneId,
      entryRule: normalizeZoneEntryRule(row.entryRule),
    });
  }
  return out;
}

/**
 * Load/normalize zoneAccess from API product for Formik.
 * Supports legacy { allowedZones } and new { zones, deviceOverrides, ... }.
 */
export function normalizeZoneAccessFromProduct(product) {
  const z = product?.zoneAccess;
  if (!z || typeof z !== "object") {
    return { ...DEFAULT_ZONE_ACCESS };
  }

  if (Array.isArray(z.zones)) {
    const zones = normalizeZonesArrayFromNewShape(z.zones);
    return {
      zones,
      allowTurnstile: z.allowTurnstile !== false,
      allowGate: z.allowGate !== false,
      deviceOverrides: normalizeDeviceOverridesArray(z.deviceOverrides),
    };
  }

  if (Array.isArray(z.allowedZones)) {
    const ids = [
      ...new Set(
        z.allowedZones.map((id) => String(id).trim()).filter(Boolean),
      ),
    ];
    return {
      zones: ids.map((zoneId) => ({
        zoneId,
        entryRule: ENTRY_SINGLE,
      })),
      allowTurnstile: z.allowTurnstile !== false,
      allowGate: z.allowGate !== false,
      deviceOverrides: normalizeDeviceOverridesArray(z.deviceOverrides),
    };
  }

  return {
    zones: [],
    allowTurnstile: z.allowTurnstile !== false,
    allowGate: z.allowGate !== false,
    deviceOverrides: normalizeDeviceOverridesArray(z.deviceOverrides),
  };
}

/**
 * Devices assigned to selected zones (from zone API payload `devices[]`), deduped by deviceId.
 * @param {object[]} allZones — full list from GET /access-system/zones
 * @param {string[]} selectedZoneIds
 * @returns {{ deviceId: string, workspaceId: string, deviceType: string, deviceName: string, zoneId: string, zoneName: string }[]} deviceName from common API fields; empty string only if none found.
 */
function resolveDeviceDisplayName(d) {
  if (!d || typeof d !== "object") return "";
  const candidates = [
    d.name,
    d.deviceName,
    d.label,
    d.title,
    d.displayName,
  ];
  for (const v of candidates) {
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return "";
}

export function eligibleDevicesFromSelectedZones(allZones, selectedZoneIds) {
  const idSet = new Set(
    (selectedZoneIds || []).map((id) => String(id).trim()).filter(Boolean),
  );
  const byZone = new Map(
    (allZones || []).map((row) => [getZoneIdFromApiRow(row), row]),
  );
  const out = [];
  const seenDevice = new Set();

  for (const zid of idSet) {
    const zone = byZone.get(zid);
    const zoneName = zone ? getZoneLabelFromApiRow(zone) : zid;
    const devices = Array.isArray(zone?.devices) ? zone.devices : [];
    for (const d of devices) {
      const norm = normalizeZoneDeviceEntryFromApi(d);
      if (!norm) continue;
      const { deviceId, workspaceId, deviceType, displayName } = norm;
      if (!deviceId || seenDevice.has(deviceId)) continue;
      seenDevice.add(deviceId);
      const deviceName =
        displayName ||
        resolveDeviceDisplayName(d) ||
        deviceId;
      out.push({
        deviceId,
        workspaceId: workspaceId || "—",
        deviceType: deviceType || "—",
        deviceName,
        zoneId: zid,
        zoneName,
      });
    }
  }
  return out;
}

/**
 * Drop override rows whose deviceId is not in the eligible set.
 */
export function pruneDeviceOverridesToEligible(deviceOverrides, eligibleDeviceIds) {
  const set =
    eligibleDeviceIds instanceof Set
      ? eligibleDeviceIds
      : new Set((eligibleDeviceIds || []).map((id) => String(id).trim()));
  const list = Array.isArray(deviceOverrides) ? deviceOverrides : [];
  const out = [];
  const seen = new Set();
  for (const row of list) {
    if (!row || typeof row !== "object") continue;
    const deviceId = String(row.deviceId ?? "").trim();
    if (!deviceId || !set.has(deviceId) || seen.has(deviceId)) continue;
    const rule = normalizeOverrideRule(row.rule);
    if (!rule) continue;
    seen.add(deviceId);
    out.push({ deviceId, rule });
  }
  return out;
}

/**
 * After zone multi-select changes: rebuild `zones` (preserve entryRule when possible),
 * prune overrides to still-eligible devices.
 */
export function mergeZoneAccessOnZoneSelection(prevZa, selectedZoneIds, allZones) {
  const prevZones = Array.isArray(prevZa?.zones) ? prevZa.zones : [];
  const prevById = new Map(
    prevZones.map((r) => [String(r.zoneId).trim(), r]),
  );
  const ids = [
    ...new Set(
      (selectedZoneIds || []).map((id) => String(id).trim()).filter(Boolean),
    ),
  ];
  const zones = ids.map((zoneId) => {
    const prev = prevById.get(zoneId);
    return {
      zoneId,
      entryRule: normalizeZoneEntryRule(prev?.entryRule),
    };
  });
  const eligibleRows = eligibleDevicesFromSelectedZones(allZones, ids);
  const eligibleIds = new Set(eligibleRows.map((r) => r.deviceId));
  const pruned = pruneDeviceOverridesToEligible(
    prevZa?.deviceOverrides,
    eligibleIds,
  );
  return {
    zones,
    allowTurnstile: prevZa?.allowTurnstile !== false,
    allowGate: prevZa?.allowGate !== false,
    deviceOverrides: pruned,
  };
}

function normalizeZonesForPayload(zonesRaw) {
  const out = [];
  const seen = new Set();
  for (const row of zonesRaw || []) {
    if (!row || typeof row !== "object") continue;
    const zoneId = String(row.zoneId ?? "").trim();
    if (!zoneId || seen.has(zoneId)) continue;
    seen.add(zoneId);
    out.push({
      zoneId,
      entryRule: normalizeZoneEntryRule(row.entryRule),
    });
  }
  return out;
}

/**
 * Canonical payload for PUT/POST product (ticket). Uses live zone list to resolve devices.
 * When no zones selected: empty arrays, flags preserved.
 */
export function sanitizeZoneAccessForPayload(za, allZonesList) {
  const base = za && typeof za === "object" ? za : {};
  const zones = normalizeZonesForPayload(base.zones);
  const selectedIds = zones.map((r) => r.zoneId);

  if (selectedIds.length === 0) {
    return {
      zones: [],
      allowTurnstile: base.allowTurnstile !== false,
      allowGate: base.allowGate !== false,
      deviceOverrides: [],
    };
  }

  if (!Array.isArray(allZonesList) || allZonesList.length === 0) {
    return {
      zones,
      allowTurnstile: base.allowTurnstile !== false,
      allowGate: base.allowGate !== false,
      deviceOverrides: normalizeDeviceOverridesArray(base.deviceOverrides),
    };
  }

  const eligible = eligibleDevicesFromSelectedZones(allZonesList, selectedIds);
  const explicit = new Map();
  for (const row of Array.isArray(base.deviceOverrides)
    ? base.deviceOverrides
    : []) {
    if (!row || typeof row !== "object") continue;
    const deviceId = String(row.deviceId ?? "").trim();
    if (!deviceId) continue;
    const rule = normalizeOverrideRule(row.rule);
    if (rule) explicit.set(deviceId, rule);
  }

  const deviceOverrides = eligible.map((d) => ({
    deviceId: d.deviceId,
    rule: explicit.get(d.deviceId) || RULE_SINGLE_USE,
  }));

  return {
    zones,
    allowTurnstile: base.allowTurnstile !== false,
    allowGate: base.allowGate !== false,
    deviceOverrides,
  };
}
